export type AppRole = 'admin' | 'manager' | 'user' | 'driver' | 'client' | 'guest';

export type AppPermission = 
  // --- Dashboard & KPIs ---
  | 'view_dashboard'
  | 'view_dashboard_kpis'
  | 'view_dashboard_charts'
  
  // --- Ordens de Serviço & Viagens ---
  | 'view_orders'
  | 'create_orders'
  | 'manage_orders'
  | 'delete_orders'
  | 'approve_orders'
  | 'print_os'
  
  // --- Campos Mínimos / Valores em OS ---
  | 'view_order_prices'     // Ver valor do frete e faturamento cobrado do cliente
  | 'edit_order_prices'     // Alterar preços e tarifas da viagem
  | 'view_order_repasse'    // Ver valor de repasse ao motorista / agregado
  | 'edit_order_repasse'    // Alterar repasse de motorista e ajudante
  | 'view_order_margin'     // Ver margem de lucro líquido da viagem
  
  // --- Roteirização & Operações ---
  | 'view_routes'
  | 'manage_routes'
  | 'create_routes'
  | 'delete_routes'
  | 'dispatch_routes'
  | 'view_driver_tracking'
  
  // --- Financeiro & Faturamento ---
  | 'view_financial'
  | 'manage_financial'
  | 'create_financial'
  | 'delete_financial'
  | 'pay_financial'
  | 'view_financial_values'
  | 'view_financial_dre'
  | 'manage_bank_gateways'
  
  // --- Medições de Clientes & Agregados ---
  | 'view_medicoes'
  | 'manage_medicoes'
  | 'create_medicoes'
  | 'delete_medicoes'
  | 'approve_medicoes'
  | 'view_medicoes_valores'
  | 'export_medicoes'
  
  // --- Frotas & Veículos ---
  | 'view_vehicles'
  | 'manage_vehicles'
  | 'create_vehicles'
  | 'delete_vehicles'
  | 'manage_checklists'
  | 'manage_maintenance'
  | 'manage_abastecimento'
  | 'view_vehicle_crlv'
  
  // --- Pessoas, Clientes & Motoristas ---
  | 'view_people'
  | 'manage_people'
  | 'create_people'
  | 'delete_people'
  | 'view_people_docs'     // Ver CPF, RG e CNPJ sem máscara
  | 'view_people_bank'     // Ver dados bancários e chaves PIX
  
  // --- Propostas Comerciais ---
  | 'view_proposals'
  | 'manage_proposals'
  | 'approve_proposals'
  
  // --- Administração & RBAC ---
  | 'manage_users'
  | 'create_user'
  | 'edit_user'
  | 'delete_user'
  | 'manage_groups'
  | 'manage_permissions'
  | 'view_audit_logs'
  | 'manage_system_settings'
  
  // --- Exportações Gerais ---
  | 'export_reports'
  
  // --- Módulo Fiscal (CT-e, NFS-e, MDF-e) ---
  | 'view_fiscal'
  | 'manage_fiscal'
  | 'emit_cte'
  | 'emit_nfse'
  | 'cancel_fiscal';

export interface PermissionDefinition {
  id: AppPermission;
  label: string;
  category: string;
  description: string;
  isSensitive?: boolean;
}

export const PERMISSION_DEFINITIONS: PermissionDefinition[] = [
  // Dashboard
  { id: 'view_dashboard', label: 'Visualizar Dashboard', category: 'Dashboard & Métricas', description: 'Acesso à página principal do dashboard' },
  { id: 'view_dashboard_kpis', label: 'Ver Faturamento e KPIs', category: 'Dashboard & Métricas', description: 'Exibe faturamento, lucro e métricas financeiras nos cards do dashboard', isSensitive: true },
  { id: 'view_dashboard_charts', label: 'Ver Gráficos Operacionais', category: 'Dashboard & Métricas', description: 'Exibe gráficos de desempenho de frota e viagens' },

  // Ordens de Serviço
  { id: 'view_orders', label: 'Visualizar Ordens de Serviço', category: 'Ordens de Serviço', description: 'Permite consultar e listar ordens de serviço' },
  { id: 'create_orders', label: 'Criar Novas Ordens de Serviço', category: 'Ordens de Serviço', description: 'Permite cadastrar novas viagens e pedidos de OS' },
  { id: 'manage_orders', label: 'Editar Ordens de Serviço', category: 'Ordens de Serviço', description: 'Permite alterar dados de origem, destino, motoristas e veículos' },
  { id: 'delete_orders', label: 'Excluir / Cancelar OS', category: 'Ordens de Serviço', description: 'Permite cancelar ou excluir viagens do sistema', isSensitive: true },
  { id: 'approve_orders', label: 'Concluir e Assinar OS', category: 'Ordens de Serviço', description: 'Permite dar baixa, anexar assinatura digital e concluir atendimento' },
  { id: 'print_os', label: 'Imprimir e Exportar OS em PDF', category: 'Ordens de Serviço', description: 'Permite gerar o PDF oficial e comprovante da OS' },

  // Campos Mínimos / Valores em OS
  { id: 'view_order_prices', label: 'Ver Valor do Frete Cobrado', category: 'Campos Específicos da OS', description: 'Permite ver o valor cobrado do cliente (total_os e subtotal)', isSensitive: true },
  { id: 'edit_order_prices', label: 'Editar Preços e Tarifas', category: 'Campos Específicos da OS', description: 'Permite alterar valores de frete, diárias e horas adicionais', isSensitive: true },
  { id: 'view_order_repasse', label: 'Ver Repasse de Motorista / Agregado', category: 'Campos Específicos da OS', description: 'Permite ver quanto será repassado ao motorista e ajudantes', isSensitive: true },
  { id: 'edit_order_repasse', label: 'Editar Repasse de Motorista', category: 'Campos Específicos da OS', description: 'Permite alterar o valor pago ao motorista e ajudante', isSensitive: true },
  { id: 'view_order_margin', label: 'Ver Margem de Lucro Líquido', category: 'Campos Específicos da OS', description: 'Exibe o valor líquido (lucro) restante da viagem', isSensitive: true },

  // Operacional & Roteiros
  { id: 'view_routes', label: 'Visualizar Roteirização', category: 'Roteirização & Operação', description: 'Permite visualizar rotas do dia e paradas dos motoristas' },
  { id: 'manage_routes', label: 'Criar e Editar Roteiros', category: 'Roteirização & Operação', description: 'Permite sequenciar coletas, entregas e associar veículos' },
  { id: 'delete_routes', label: 'Excluir Roteiros', category: 'Roteirização & Operação', description: 'Permite deletar roteiros planejados' },
  { id: 'dispatch_routes', label: 'Despachar Rota ao Motorista', category: 'Roteirização & Operação', description: 'Envia o roteiro para o app do motorista em tempo real' },
  { id: 'view_driver_tracking', label: 'Rastreamento GPS ao Vivo', category: 'Roteirização & Operação', description: 'Acesso à localização geográfica e telemetria dos motoristas' },

  // Financeiro
  { id: 'view_financial', label: 'Visualizar Financeiro', category: 'Financeiro & Contas', description: 'Acesso ao módulo de Contas a Pagar e Receber' },
  { id: 'view_financial_values', label: 'Ver Valores Financeiros', category: 'Financeiro & Contas', description: 'Visualiza saldos totais, valores de títulos e fluxo de caixa', isSensitive: true },
  { id: 'manage_financial', label: 'Editar Lançamentos Financeiros', category: 'Financeiro & Contas', description: 'Permite cadastrar e alterar títulos e faturas' },
  { id: 'create_financial', label: 'Criar Nova Conta / Despesa', category: 'Financeiro & Contas', description: 'Lançar novas contas a pagar ou a receber' },
  { id: 'delete_financial', label: 'Excluir Títulos Financeiros', category: 'Financeiro & Contas', description: 'Excluir lançamentos e movimentações financeiras', isSensitive: true },
  { id: 'pay_financial', label: 'Baixar e Liquidar Títulos', category: 'Financeiro & Contas', description: 'Registrar pagamento ou recebimento de contas' },
  { id: 'view_financial_dre', label: 'Visualizar DRE e Demonstrativos', category: 'Financeiro & Contas', description: 'Acesso a relatórios contábeis e demonstrativos de resultado', isSensitive: true },
  { id: 'manage_bank_gateways', label: 'Configurar Contas Bancárias & PIX', category: 'Financeiro & Contas', description: 'Configurar credenciais bancárias e gateways de cobrança', isSensitive: true },

  // Medições
  { id: 'view_medicoes', label: 'Visualizar Medições', category: 'Medições & Faturamento', description: 'Acesso às medições periódicas de clientes e agregados' },
  { id: 'view_medicoes_valores', label: 'Ver Valores de Medição', category: 'Medições & Faturamento', description: 'Visualiza os montantes financeiros consolidados na medição', isSensitive: true },
  { id: 'manage_medicoes', label: 'Gerar / Editar Medições', category: 'Medições & Faturamento', description: 'Permite criar novos fechamentos de medição e agrupar OSs' },
  { id: 'delete_medicoes', label: 'Excluir Medições', category: 'Medições & Faturamento', description: 'Excluir períodos de medição calculados' },
  { id: 'approve_medicoes', label: 'Aprovar / Fechar Medições', category: 'Medições & Faturamento', description: 'Aprovar faturamento de medição para envio de fatura' },
  { id: 'export_medicoes', label: 'Exportar Relatório de Medição', category: 'Medições & Faturamento', description: 'Exportar medições em Excel e PDF' },

  // Frotas
  { id: 'view_vehicles', label: 'Visualizar Frota de Veículos', category: 'Gestão de Frotas', description: 'Acesso ao cadastro e lista de veículos' },
  { id: 'manage_vehicles', label: 'Cadastrar / Editar Veículos', category: 'Gestão de Frotas', description: 'Permite alterar dados de veículos, motoristas e placas' },
  { id: 'delete_vehicles', label: 'Excluir / Inativar Veículos', category: 'Gestão de Frotas', description: 'Inativar veículos da frota', isSensitive: true },
  { id: 'manage_checklists', label: 'Auditar Checklists de Veículos', category: 'Gestão de Frotas', description: 'Aprovar vistorias de pneus, óleo, lataria e freios' },
  { id: 'manage_maintenance', label: 'Gerenciar Manutenções', category: 'Gestão de Frotas', description: 'Lançar ordens de manutenção preventiva e corretiva' },
  { id: 'manage_abastecimento', label: 'Lançar Abastecimentos', category: 'Gestão de Frotas', description: 'Registrar litros, valores e odômetro de combustível' },
  { id: 'view_vehicle_crlv', label: 'Visualizar Documentos CRLV', category: 'Gestão de Frotas', description: 'Acessar CRLVs digitais e vencimentos de IPVA/licenciamento' },

  // Pessoas
  { id: 'view_people', label: 'Visualizar Pessoas e Parceiros', category: 'Pessoas & Clientes', description: 'Consultar lista de clientes, fornecedores e motoristas' },
  { id: 'manage_people', label: 'Cadastrar / Editar Pessoas', category: 'Pessoas & Clientes', description: 'Criar e editar cadastros de parceiros e clientes' },
  { id: 'delete_people', label: 'Inativar / Excluir Pessoas', category: 'Pessoas & Clientes', description: 'Excluir cadastros do sistema', isSensitive: true },
  { id: 'view_people_docs', label: 'Ver CPF / RG / CNPJ Completo', category: 'Pessoas & Clientes', description: 'Exibe documentos pessoais e fiscais sem mascaramento', isSensitive: true },
  { id: 'view_people_bank', label: 'Ver Dados Bancários e Chave PIX', category: 'Pessoas & Clientes', description: 'Acesso a conta, agência e chave PIX para pagamentos', isSensitive: true },

  // Propostas Comerciais
  { id: 'view_proposals', label: 'Visualizar Propostas Comerciais', category: 'Comercial & Propostas', description: 'Consultar orçamentos e propostas emitidas' },
  { id: 'manage_proposals', label: 'Criar / Editar Propostas', category: 'Comercial & Propostas', description: 'Elaborar novas cotações e propostas de frete' },
  { id: 'approve_proposals', label: 'Aprovar e Converter em Pedido', category: 'Comercial & Propostas', description: 'Aprovar proposta para gerar ordens de serviço automaticamente' },

  // Administração e Segurança
  { id: 'manage_users', label: 'Acessar Gestão de Usuários', category: 'Segurança & Administração', description: 'Acesso ao módulo de usuários e permissões do sistema', isSensitive: true },
  { id: 'create_user', label: 'Criar Novos Usuários', category: 'Segurança & Administração', description: 'Cadastrar novos logins no Supabase Auth e system_users', isSensitive: true },
  { id: 'edit_user', label: 'Editar Usuários e Perfis', category: 'Segurança & Administração', description: 'Alterar papéis, grupos e dados de login', isSensitive: true },
  { id: 'delete_user', label: 'Excluir / Bloquear Usuários', category: 'Segurança & Administração', description: 'Desativar ou deletar acessos de usuários', isSensitive: true },
  { id: 'manage_groups', label: 'Gerenciar Grupos de Acesso', category: 'Segurança & Administração', description: 'Criar e configurar grupos e programas atribuídos', isSensitive: true },
  { id: 'manage_permissions', label: 'Configurar Permissões Granulares', category: 'Segurança & Administração', description: 'Conceder ou revogar permissões mínimas por usuário', isSensitive: true },
  { id: 'view_audit_logs', label: 'Ver Logs de Auditoria', category: 'Segurança & Administração', description: 'Consultar histórico de alterações no sistema' },
  { id: 'manage_system_settings', label: 'Configurações Globais do Sistema', category: 'Segurança & Administração', description: 'Ajustar parâmetros operacionais e de faturamento', isSensitive: true },

  // Exportações
  { id: 'export_reports', label: 'Exportar Relatórios Gerais', category: 'Relatórios & Exportações', description: 'Permite exportar dados em Excel, CSV e PDF' },

  // Módulo Fiscal
  { id: 'view_fiscal', label: 'Visualizar Documentos Fiscais', category: 'Fiscal & Faturamento', description: 'Consultar CT-e, NFS-e e MDF-e emitidos' },
  { id: 'manage_fiscal', label: 'Gerenciar Parâmetros Fiscais', category: 'Fiscal & Faturamento', description: 'Configurar ambientes de emissão e parâmetros SEFAZ/Prefeitura', isSensitive: true },
  { id: 'emit_cte', label: 'Emitir Conhecimento de Transporte (CT-e)', category: 'Fiscal & Faturamento', description: 'Transmitir CT-e modelo 57 para autorização na SEFAZ', isSensitive: true },
  { id: 'emit_nfse', label: 'Emitir Nota Fiscal de Serviços (NFS-e)', category: 'Fiscal & Faturamento', description: 'Transmitir NFS-e para a prefeitura após medições', isSensitive: true },
  { id: 'cancel_fiscal', label: 'Cancelar Documentos Fiscais', category: 'Fiscal & Faturamento', description: 'Realizar cancelamento formal de CT-e ou NFS-e perante o fisco', isSensitive: true }
];

export interface UserSession {
  id: number | string;
  name: string;
  email: string | null;
  role: AppRole;
  permissions: AppPermission[];
}


